import { Typography, InputNumber, Select, Row, Col, Input } from 'antd';

import { Form, WrapperSelect } from 'common/components';
import {
  FormItemContainer,
  FormItemDecimalNumber,
  FormItemText,
  PropertyInfo,
  PropertyLabel,
} from 'pages/product-full-view/shared/components';

import { useGetEnumsByEnumName } from 'hooks';

import sortByKey from 'utils/sortByKey';

import { getDataByFieldFullPath } from 'pages/product-full-view/shared/utils';
import {
  OPTICS_THERMAL_HEIGHT,
  OPTICS_THERMAL_WIDTH,
} from 'pages/product-full-view/components/product-new-detail-view/constants';

export const FormItemElevationTurretTravel = (props) => {
  const { property, ...otherProps } = props;

  const suffixEnums = useGetEnumsByEnumName(
    'ElevationTurretTravelOpticsRifleEnum'
  );

  const suffixSelector = (
    <Form.Item name='ElevationTurretTravelPostfix' noStyle initialValue='MOA'>
      <WrapperSelect
        listHeight={140}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        style={{ width: 100 }}
        defaultValue='MOA'
        allowClear={false}
      >
        {sortByKey(suffixEnums, 'enumDisplayName')?.map((property, index) => (
          <Select.Option
            value={property.enumDisplayName}
            key={property.enumDisplayName + index}
          >
            {property.enumDisplayName
              ? property.enumDisplayName
              : property.enumDescription}
          </Select.Option>
        ))}
      </WrapperSelect>
    </Form.Item>
  );

  return (
    <>
      <FormItemContainer property={property} {...otherProps}>
        <InputNumber
          addonAfter={suffixSelector}
          placeholder={`Please Input ${property.propertyDisplayName}`}
          style={{
            width: '100%',
          }}
        />
      </FormItemContainer>
      <Form.Item name='ElevationTurretTravelPostfix' hidden>
        <Input />
      </Form.Item>
    </>
  );
};

export const FormItemWindageTurretTravel = (props) => {
  const { property, ...otherProps } = props;

  const suffixEnums = useGetEnumsByEnumName(
    'ElevationTurretTravelOpticsRifleEnum'
  );

  const suffixSelector = (
    <Form.Item name='WindageTurretTravelPostfix' noStyle initialValue='MOA'>
      <WrapperSelect
        listHeight={140}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        style={{ width: 100 }}
        defaultValue='MOA'
        allowClear={false}
      >
        {sortByKey(suffixEnums, 'enumDisplayName')?.map((property, index) => (
          <Select.Option
            value={property.enumDisplayName}
            key={property.enumDisplayName + index}
          >
            {property.enumDisplayName
              ? property.enumDisplayName
              : property.enumDescription}
          </Select.Option>
        ))}
      </WrapperSelect>
    </Form.Item>
  );

  return (
    <>
      <FormItemContainer property={property} {...otherProps}>
        <InputNumber
          addonAfter={suffixSelector}
          placeholder={`Please Input ${property.propertyDisplayName}`}
          style={{
            width: '100%',
          }}
        />
      </FormItemContainer>
      <Form.Item name='WindageTurretTravelPostfix' hidden>
        <Input />
      </Form.Item>
    </>
  );
};

export const FormItemMagnification = (props) => {
  const { property, ...otherProps } = props;

  return (
    <FormItemContainer property={property} {...otherProps}>
      <InputNumber
        addonAfter={<Typography.Text strong>ft @ 100 Yards</Typography.Text>}
        placeholder={`Please Input ${property.propertyDisplayName}`}
        style={{
          width: '100%',
        }}
      />
    </FormItemContainer>
  );
};

export const FormItemLength = (props) => {
  const { property } = props;

  return (
    <FormItemDecimalNumber
      step={0.1}
      precision={1}
      property={property}
      suffix={<Typography.Text strong>in.</Typography.Text>}
    />
  );
};

export const FormItemWeight = (props) => {
  const { property } = props;

  return (
    <FormItemDecimalNumber
      step={0.1}
      precision={1}
      property={property}
      suffix={<Typography.Text strong>oz.</Typography.Text>}
    />
  );
};

export const FormItemHeight = (props) => {
  const { property } = props;

  return (
    <FormItemDecimalNumber step={0.01} precision={2} property={property} />
  );
};

export const FormItemEyeRelief = (props) => {
  const { property } = props;

  return <FormItemDecimalNumber step={0.1} precision={1} property={property} />;
};

export const FormItemFocusRange = (props) => {
  const { property } = props;

  return <FormItemDecimalNumber step={0.1} precision={1} property={property} />;
};

export const FormItemMagnificationDigital = (props) => {
  const { property } = props;

  return (
    <FormItemDecimalNumber
      step={0.1}
      precision={1}
      property={property}
      formatter={(value) => (value ? `${value}x` : '')}
      parser={(value) => (value ? value.replace('x', '') : '')}
    />
  );
};

export const FormItemSensorResolution = ({ property, ...otherProps }) => {
  return (
    <FormItemContainer
      property={{ ...property, propertyDisplayName: 'Sensor Resolution' }}
      {...otherProps}
    >
      <Input.Group compact style={{ display: 'flex', alignItems: 'center' }}>
        <FormItemText maxLength={4} noStyle property={property} />
        <span
          style={{
            padding: '0 8px',
            color: '#8c8c8c',
            fontSize: '16px',
          }}
        >
          ×
        </span>
        <FormItemText
          maxLength={4}
          noStyle
          property={{
            propertyName: 'SensorResolutionHeight',
            propertyDisplayName: 'Sensor Resolution Height',
          }}
        />
      </Input.Group>
    </FormItemContainer>
  );
};

export const FormItemSizeIn = ({ property, ...otherProps }) => {
  return (
    <FormItemContainer
      property={{ ...property, propertyDisplayName: 'Size In' }}
      {...otherProps}
    >
      <Input.Group compact style={{ display: 'flex', alignItems: 'center' }}>
        <FormItemDecimalNumber
          step={0.1}
          precision={1}
          property={property}
          noStyle
        />
        <span
          style={{
            padding: '0 8px',
            color: '#8c8c8c',
            fontSize: '16px',
          }}
        >
          ×
        </span>
        <FormItemDecimalNumber
          step={0.1}
          precision={1}
          noStyle
          property={{
            propertyName: 'Height',
            propertyDisplayName: 'Height In.',
          }}
        />
        <span
          style={{
            padding: '0 8px',
            color: '#8c8c8c',
            fontSize: '16px',
          }}
        >
          ×
        </span>
        <FormItemDecimalNumber
          step={0.1}
          precision={1}
          noStyle
          property={{
            propertyName: 'Width',
            propertyDisplayName: 'Width In.',
          }}
        />
      </Input.Group>
    </FormItemContainer>
  );
};

/*
  Display components
*/

export const PropertyNumberElevationTurretTravel = ({ property, data }) => {
  const elevationTurretTravelPostfixValue = getDataByFieldFullPath(
    data,
    'AttributesOpticsRifle.ElevationTurretTravelPostfix'
  );

  return (
    <>
      <PropertyNumberWithSuffix
        property={property}
        data={data}
        suffix={` ${elevationTurretTravelPostfixValue}`}
      />
    </>
  );
};

export const PropertyNumberWindageTurretTravel = ({ property, data }) => {
  const windageTurretTravelPostfixPostfixValue = getDataByFieldFullPath(
    data,
    'AttributesOpticsRifle.WindageTurretTravelPostfix'
  );

  return (
    <>
      <PropertyNumberWithSuffix
        property={property}
        data={data}
        suffix={` ${windageTurretTravelPostfixPostfixValue}`}
      />
    </>
  );
};

export const PropertyNumberWithSuffix = ({ property, data, suffix }) => {
  const { propertyDisplayName, fieldFullPath } = property;

  const value = getDataByFieldFullPath(data, fieldFullPath);
  const isValidNumber = value !== null && value !== undefined;

  return (
    <>
      {isValidNumber && (
        <Row>
          <Col span={12}>
            <PropertyLabel>{propertyDisplayName}:</PropertyLabel>
          </Col>
          <Col flex='auto'>
            <PropertyInfo>
              {value}
              <Typography.Text>{suffix}</Typography.Text>
            </PropertyInfo>
          </Col>
        </Row>
      )}
    </>
  );
};

export const PropertyNumberSensorResolution = ({ property, data }) => {
  const height = getDataByFieldFullPath(
    data,
    'AttributesOpticsNightVision.SensorResolutionHeight'
  );

  return (
    <>
      <PropertyNumberWithSuffix
        property={{ ...property, propertyDisplayName: 'Sensor Resolution' }}
        data={data}
        suffix={` x ${height || 'N/A'}`}
      />
    </>
  );
};

export const PropertyNumberSize = ({ property, data }) => {
  const height = getDataByFieldFullPath(data, OPTICS_THERMAL_HEIGHT);
  const width = getDataByFieldFullPath(data, OPTICS_THERMAL_WIDTH);

  return (
    <>
      <PropertyNumberWithSuffix
        property={{ ...property, propertyDisplayName: 'Size In' }}
        data={data}
        suffix={` x ${height || 'N/A'} x ${width || 'N/A'}`}
      />
    </>
  );
};
